import React, { useState, useEffect } from "react";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../../../../core/data/redux/apis/urls";
import {
  formatDate,
  formatNatureOfDuty,
  formatCourseType,
  formatCourseMode,
  formatCourseLocation,
} from "./common/nocUtils";
import { handleDocumentClick } from "./common/documentUtils";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocDandARCard } from "./common/NocDandARCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";
import { DnarOption } from "./common/types";

interface NocHigherStudyApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  dnarMasterList: DnarOption[];
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocHigherStudyApproval: React.FC<NocHigherStudyApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  dnarMasterList,
  onShowToast,
}) => {
  // Editable fields
  const [courseName, setCourseName] = useState<string>("");
  const [institute, setInstitute] = useState<string>("");
  const [partTimeOrCorresp, setPartTimeOrCorresp] = useState<string>("");

  // D&AR State
  const [dnARStatus, setDnARStatus] = useState<string>("");
  const [dnARPenaltyEffective, setDnARPenaltyEffective] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");

  // Approver Remarks (hRemarks)
  const [hRemarks, setHRemarks] = useState<string>("");

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  const getFieldStatus = (fieldName: string): "EDITABLE" | "READONLY" | "HIDDEN" => {
    const rawVal =
      nocData?.enabled_fields_fields_json?.[fieldName] ||
      nocData?.enabled_fields_json?.[fieldName] ||
      nocData?.[fieldName];

    if (rawVal === "X" || rawVal === "V" || rawVal === true) return "EDITABLE";
    return "READONLY";
  };

  useEffect(() => {
    if (nocData) {
      setCourseName(nocData.courseName || "");
      setInstitute(nocData.institute || "");
      setPartTimeOrCorresp(nocData.partTimeOrCorresp || nocData.modeOfCourse || "");
      setDnARStatus(nocData?.dnARStatus || nocData?.DnARStatus || nocData?.dandARStatus || "");
      setDnARPenaltyEffective(nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || nocData?.dandARPenaltyEffective || "");
      const initialRemarks =
        (nocData?.Remarks !== "X" && nocData?.Remarks) ||
        (nocData?.remarks !== "X" && nocData?.remarks) ||
        "";
      setRemarks(initialRemarks);
      setHRemarks("");
    }
  }, [nocData]);

  const handleApproveReject = async (action: "A" | "R") => {
    // Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const payload = {
        Approve_Reject_flag: action,
        ReferenceNumber: nocData.referenceNumber,
        DnARStatus: dnARStatus || undefined,
        dandARStatus: dnARStatus || undefined,
        DnARPenaltyEffecti: dnARPenaltyEffective || undefined,
        dandARPenaltyEffective: dnARPenaltyEffective || undefined,
        remarks: "",
        hRemarks: (hRemarks || "").trim(),
        courseName: courseName || nocData.courseName || "",
        institute: institute || nocData.institute || "",
        partTimeOrCorresp: partTimeOrCorresp || nocData.partTimeOrCorresp || nocData.modeOfCourse || "",
      };

      console.log("Higher Study Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc-higherstudies/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Higher Education NOC application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Higher Study Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const isCourseNameEditable =
    nocData?.enabled_fields_json?.CourseName === "X" ||
    nocData?.enabled_fields_fields_json?.CourseName === "X";

  const isInstituteEditable =
    nocData?.enabled_fields_json?.Institute === "X" ||
    nocData?.enabled_fields_fields_json?.Institute === "X";

  const isModeEditable =
    nocData?.enabled_fields_json?.PartTimeOrCorresp === "X" ||
    nocData?.enabled_fields_fields_json?.PartTimeOrCorresp === "X";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="NOC for Higher Education Approval"
          breadcrumbTitle="Higher Education Request Details"
          nocType="NOC_HIGHERSTUDY"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Info */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_HIGHERSTUDY"
        />

        {/* Higher Study Details Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-school me-2 text-primary"></i>
              Higher Education Specific Information
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Nature of Duty Performing
                </label>
                <div className="fw-bold text-dark">
                  {formatNatureOfDuty(nocData?.natureOfDutyPerfo, nocData?.nature_of_duty)}
                </div>
              </div>

              {/* Course Name */}
              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Name of the Study Course
                </label>
                {isCourseNameEditable ? (
                  <input
                    type="text"
                    className="form-control"
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{courseName || "-"}</div>
                )}
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Type of Course
                </label>
                <div className="fw-bold text-dark">{formatCourseType(nocData)}</div>
              </div>

              {nocData?.otherThanDiploma && nocData?.otherThanDiploma !== "other" && (
                <div className="col-md-6 col-sm-6">
                  <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                    Specify Other Type of Course
                  </label>
                  <div className="fw-bold text-dark">{nocData.otherThanDiploma}</div>
                </div>
              )}

              {/* Mode of Course */}
              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Mode of Course
                </label>
                {isModeEditable ? (
                  <select
                    className="form-select"
                    value={partTimeOrCorresp}
                    onChange={(e) => setPartTimeOrCorresp(e.target.value)}
                  >
                    <option value="">Select Mode...</option>
                    <option value="P">Part Time</option>
                    <option value="C">Correspondence</option>
                    <option value="F">Full Time</option>
                    <option value="R">Regular</option>
                  </select>
                ) : (
                  <div className="fw-bold text-dark">{formatCourseMode(nocData)}</div>
                )}
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Duration of Course
                </label>
                <div className="fw-bold text-dark">
                  {nocData?.durationYears || 0}Y {nocData?.monthsCourseDuration || 0}M
                </div>
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Date of Joining Course
                </label>
                <div className="fw-bold text-dark">{formatDate(nocData?.dateOfCourseJoining)}</div>
              </div>

              {/* Institute Name */}
              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Institute Name
                </label>
                {isInstituteEditable ? (
                  <input
                    type="text"
                    className="form-control"
                    value={institute}
                    onChange={(e) => setInstitute(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{institute || "N/A"}</div>
                )}
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Last Date of Registration for Course
                </label>
                <div className="fw-bold text-dark">{formatDate(nocData?.lastDateOfRegistration)}</div>
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Course Ending Date (Tentative)
                </label>
                <div className="fw-bold text-dark">{formatDate(nocData?.courseEndingDate)}</div>
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Proposed Course is Pursuing In
                </label>
                <div className="fw-bold text-dark">{formatCourseLocation(nocData?.inIndiaOrNot)}</div>
              </div>

              <div className="col-md-6 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Timing of Classes
                </label>
                <div className="fw-bold text-dark">{nocData?.timingOfClasses || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Questions Checklist Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-help-circle me-2 text-primary"></i>
              Questions & Declarations Checklist
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-dark fw-medium">Whether probation is confirmed in DMRC</span>
                <span className={`badge ${nocData?.probationConfirmed?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.probationConfirmed?.toUpperCase() === "Y" ? "Yes" : nocData?.probationConfirmed?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-dark fw-medium">
                  Whether currently pursuing any higher education course, for which NOC was granted
                </span>
                <span className={`badge ${nocData?.currentlyPursuingA?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.currentlyPursuingA?.toUpperCase() === "Y" ? "Yes" : nocData?.currentlyPursuingA?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-dark fw-medium">
                  Whether institution is approved by concerned Government Body like UGC/AICTE/DEC
                </span>
                <span className={`badge ${nocData?.isGovernmentBody?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.isGovernmentBody?.toUpperCase() === "Y" ? "Yes" : nocData?.isGovernmentBody?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-dark fw-medium">
                  Whether course is approved by concerned Government Body like UGC/AICTE/DEC
                </span>
                <span className={`badge ${nocData?.isCourseRecogByGov?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.isCourseRecogByGov?.toUpperCase() === "Y" ? "Yes" : nocData?.isCourseRecogByGov?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-dark fw-medium">Is the course relevant to your employment</span>
                <span className={`badge ${nocData?.isReleventToEmployee?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.isReleventToEmployee?.toUpperCase() === "Y" ? "Yes" : nocData?.isReleventToEmployee?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2">
                <span className="text-dark fw-medium">
                  Whether any other NOC has been granted during employment in DMRC for any course
                </span>
                <span className={`badge ${nocData?.whetherAnotherNoc?.toUpperCase() === "Y" ? "bg-success" : "bg-danger"}`}>
                  {nocData?.whetherAnotherNoc?.toUpperCase() === "Y" ? "Yes" : nocData?.whetherAnotherNoc?.toUpperCase() === "N" ? "No" : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Attachments Card */}
        {Array.isArray(nocData?.documents) && nocData.documents.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-paperclip me-2 text-primary"></i>
                Application Attachments
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {nocData.documents.map((doc: any, idx: number) => (
                  <button
                    key={doc.id || idx}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center"
                    onClick={() => handleDocumentClick(doc, "NOC_HIGHERSTUDY", onShowToast)}
                  >
                    <i className="ti ti-file me-1"></i>
                    {doc.fileName?.split("/").pop() || `Attachment ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Master Circular Note */}
        <div className="alert alert-info border-0 text-center py-2.5 px-3 mb-4 small fw-medium rounded">
          <i className="ti ti-info-circle me-1.5 fs-15"></i>
          *Condition of 03 years Regular Service in DMRC dispensed away with (as per Master Circular).
        </div>

        {/* Work History */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* D&AR Status */}
        <NocDandARCard
          nocData={nocData}
          nocType="NOC_HIGHERSTUDY"
          dnARStatus={dnARStatus}
          dnARPenaltyEffective={dnARPenaltyEffective}
          remarks={remarks}
          dnarMasterList={dnarMasterList}
          onStatusChange={setDnARStatus}
          onPenaltyEffectiveChange={setDnARPenaltyEffective}
          onRemarksChange={setRemarks}
          getFieldStatus={getFieldStatus}
        />

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_HIGHERSTUDY"
          onShowToast={onShowToast}
        />

        {/* Actions Modal */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
